import api from './api';

// ─── Client Service ───────────────────────────────────────────────────────────

export const getClientProfile = async () => {
  const response = await api.get('/client/profile');
  return response.data;
};

export const getClientSites = async () => {
  const response = await api.get('/client/sites');
  return response.data;
};

export const getSiteDashboard = async (siteId) => {
  const response = await api.get(`/client/sites/${siteId}/dashboard`);
  return response.data;
};

export const getCustomFolders = async (siteId, month, year) => {
  const response = await api.get('/admin/custom-folders', {
    params: { site_id: siteId, month, year },
  });
  return response.data;
};

// ─── Admin – Create (multipart/form-data) ────────────────────────────────────

export const createClient = async (formData) => {
  const response = await api.post('/admin/clients', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return response.data;
};

export const createSite = async (siteData) => {
  const response = await api.post('/admin/sites', siteData);
  return response.data;
};

export const uploadMonthlyData = async (formData) => {
  const response = await api.post('/admin/monthly-data', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return response.data;
};

// ─── Direct S3 Multipart Upload ──────────────────────────────────────────────

export const startMultipartUpload = async ({
  folder,
  originalName,
  mimeType,
}) => {
  const response = await api.post('/admin/multipart/start', {
    folder,
    originalName,
    mimeType,
  });

  return response.data;
};

export const getMultipartPartUrl = async ({
  key,
  uploadId,
  partNumber,
}) => {
  const response = await api.get('/admin/multipart/part-url', {
    params: {
      key,
      uploadId,
      partNumber,
    },
  });

  return response.data.url;
};

export const completeMultipartUpload = async ({
  key,
  uploadId,
  parts,
}) => {
  const response = await api.post('/admin/multipart/complete', {
    key,
    uploadId,
    parts,
  });

  return response.data;
};

export const abortMultipartUpload = async ({
  key,
  uploadId,
}) => {
  const response = await api.post('/admin/multipart/abort', {
    key,
    uploadId,
  });

  return response.data;
};


export const uploadFinalProduct = async (formData) => {
  const response = await api.post('/admin/final-product', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return response.data;
};

export const saveMultipartVideo = async ({
  update_id,
  title,
  video_type,
  is_360,
  video_url,
  s3_key,
  custom_folder_name,
}) => {
  const response = await api.post('/admin/multipart/video', {
    update_id,
    title,
    video_type,
    is_360,
    video_url,
    s3_key,
    custom_folder_name,
  });

  return response.data;
};

export const uploadFileToS3Multipart = async ({
  file,
  folder,
  onProgress,
}) => {
  const PART_SIZE = 25 * 1024 * 1024; // 25 MB
  const CONCURRENCY = 4;

  let multipart = null;

  try {
    // 1. Start multipart upload
    multipart = await startMultipartUpload({
      folder,
      originalName: file.name,
      mimeType: file.type || 'application/octet-stream',
    });

    const { key, uploadId } = multipart;

    const totalParts = Math.ceil(file.size / PART_SIZE);
    const completedParts = new Array(totalParts);

    // Track exact uploaded bytes for every parallel part
    const partProgress = new Array(totalParts).fill(0);

    const updateProgress = () => {
      if (!onProgress) return;

      const uploadedBytes = partProgress.reduce(
        (total, bytes) => total + bytes,
        0
      );

      const progress = Math.min(
        100,
        Math.round((uploadedBytes / file.size) * 100)
      );

      onProgress(progress);
    };

    // Upload one part with real browser upload progress
    const uploadPart = async (partNumber) => {
      const start = (partNumber - 1) * PART_SIZE;
      const end = Math.min(start + PART_SIZE, file.size);
      const chunk = file.slice(start, end);

      const url = await getMultipartPartUrl({
        key,
        uploadId,
        partNumber,
      });

      const etag = await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();

        xhr.open('PUT', url, true);

        xhr.upload.onprogress = (event) => {
          if (event.lengthComputable) {
            partProgress[partNumber - 1] = event.loaded;
            updateProgress();
          }
        };

        xhr.onload = () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            const responseEtag = xhr.getResponseHeader('ETag');

            if (!responseEtag) {
              reject(new Error(`ETag missing for part ${partNumber}`));
              return;
            }

            // Make sure final bytes are counted
            partProgress[partNumber - 1] = chunk.size;
            updateProgress();

            resolve(responseEtag);
          } else {
            reject(
              new Error(
                `S3 upload failed for part ${partNumber}: ${xhr.status}`
              )
            );
          }
        };

        xhr.onerror = () => {
          reject(
            new Error(`Network error while uploading part ${partNumber}`)
          );
        };

        xhr.onabort = () => {
          reject(
            new Error(`Upload aborted for part ${partNumber}`)
          );
        };

        xhr.send(chunk);
      });

      completedParts[partNumber - 1] = {
        ETag: etag,
        PartNumber: partNumber,
      };
    };

    // 2. Upload multiple parts in parallel
    let nextPart = 1;

    const worker = async () => {
      while (true) {
        const partNumber = nextPart++;

        if (partNumber > totalParts) {
          return;
        }

        await uploadPart(partNumber);
      }
    };

    const workers = Array.from(
      { length: Math.min(CONCURRENCY, totalParts) },
      () => worker()
    );

    await Promise.all(workers);

    // Ensure UI reaches 100%
    if (onProgress) {
      onProgress(100);
    }

    // 3. Complete multipart upload
    const result = await completeMultipartUpload({
      key,
      uploadId,
      parts: completedParts,
    });

    return result;
  } catch (error) {
    if (multipart?.key && multipart?.uploadId) {
      try {
        await abortMultipartUpload({
          key: multipart.key,
          uploadId: multipart.uploadId,
        });
      } catch (abortError) {
        console.error('Multipart abort failed:', abortError);
      }
    }

    throw error;
  }
};


// ─── Admin – Client CRUD ──────────────────────────────────────────────────────

export const getAllClients = async () => {
  const response = await api.get('/admin/clients');
  return response.data;
};

export const getClientById = async (clientId) => {
  const response = await api.get(`/admin/clients/${clientId}`);
  return response.data;
};

export const updateClient = async (clientId, data) => {
  const response = await api.put(`/admin/clients/${clientId}`, data);
  return response.data;
};

export const updateClientPassword = async (clientId, newPassword) => {
  const response = await api.put(`/admin/clients/${clientId}/password`, { newPassword });
  return response.data;
};

export const deleteClient = async (clientId) => {
  const response = await api.delete(`/admin/clients/${clientId}`);
  return response.data;
};

// ─── Admin – Site CRUD ────────────────────────────────────────────────────────

export const getAllSites = async () => {
  const response = await api.get('/admin/sites');
  return response.data;
};

export const getSiteById = async (siteId) => {
  const response = await api.get(`/admin/sites/${siteId}`);
  return response.data;
};

export const updateSite = async (siteId, data) => {
  const response = await api.put(`/admin/sites/${siteId}`, data);
  return response.data;
};

export const deleteSite = async (siteId) => {
  const response = await api.delete(`/admin/sites/${siteId}`);
  return response.data;
};

// ─── Admin – Monthly Update CRUD ─────────────────────────────────────────────

export const getAllMonthlyUpdates = async () => {
  const response = await api.get('/admin/monthly-updates');
  return response.data;
};

export const updateMonthlyUpdate = async (updateId, data) => {
  const response = await api.put(`/admin/monthly-updates/${updateId}`, data);
  return response.data;
};

export const deleteMonthlyUpdate = async (updateId) => {
  const response = await api.delete(`/admin/monthly-updates/${updateId}`);
  return response.data;
};

// ─── Admin – Final Product CRUD ───────────────────────────────────────────────

export const getAllFinalProducts = async () => {
  const response = await api.get('/admin/final-products');
  return response.data;
};

export const updateFinalProduct = async (productId, data) => {
  const response = await api.put(`/admin/final-products/${productId}`, data);
  return response.data;
};

export const deleteFinalProduct = async (productId) => {
  const response = await api.delete(`/admin/final-products/${productId}`);
  return response.data;
};
